import { Link, useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import DeleteModal from '../../components/DeleteModal';
import { useAuth, api } from '../../context/AuthContext';
import '../../styles/user/IndividualUser.css';
import '../../styles/user/SubscriptionStyles.css';


// Create User Modal Component
const CreateUserModal = ({ isOpen, onClose, onConfirm, user, pricingPlans, selectedPlan, setSelectedPlan, selectedTrialDuration, setSelectedTrialDuration, selectedTierId, setSelectedTierId, processing, userCreationResponse }) => {
    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                {!userCreationResponse ? (
                    <>
                        <div className="modal-header">
                            <h3>Opprett Bruker med Abonnement</h3>
                            <button className="modal-close" onClick={onClose}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="user-info">
                                <p><strong>Navn:</strong> {user?.name}</p>
                                <p><strong>E-post:</strong> {user?.email}</p>
                                <p><strong>Telefon:</strong> {user?.phone_number || 'Ikke oppgitt'}</p>
                            </div>
                            
                            <div className="plan-selection">
                                <label>Velg Prisplan:</label>
                                <select 
                                    value={`${selectedPlan}-${selectedTierId || ''}`}
                                    onChange={(e) => {
                                        const [planId, tierId] = e.target.value.split('-');
                                        setSelectedPlan(planId);
                                        setSelectedTierId(tierId);
                                    }}
                                    disabled={processing}
                                >
                                    <option value="">Velg en plan</option>
                                    {pricingPlans.map(plan => (
                                        plan.tiers_mode === 1 && plan.tiers && plan.tiers.length > 0 ? (
                                            plan.tiers.map(tier => (
                                                <option 
                                                    key={`${plan.idpricing}-${tier.id}`} 
                                                    value={`${plan.idpricing}-${tier.id}`}
                                                >
                                                    {plan.name} - {tier.amount} NOK - {tier.interval_count} {tier.payment_interval}
                                                    {tier.interval_count > 1 ? 'er' : ''}
                                                </option>
                                            ))
                                        ) : null
                                    ))}
                                </select>

                                <div className="trial-duration-slider">
                                    <label>Prøveperiode: {selectedTrialDuration} dager</label>
                                    <input
                                        type="range"
                                        min="1"
                                        max="365"
                                        value={selectedTrialDuration}
                                        onChange={(e) => setSelectedTrialDuration(Number(e.target.value))}
                                        className="trial-slider"
                                        disabled={processing}
                                    />
                                    <div className="trial-duration-markers">
                                        <span>1 dag</span>
                                        <span>180 dager</span>
                                        <span>365 dager</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-actions">
                            <button
                                onClick={onClose}
                                className="modal-button cancel"
                                disabled={processing}
                            >
                                Avbryt
                            </button>
                            <button
                                onClick={onConfirm}
                                disabled={processing || !selectedPlan || !selectedTrialDuration || !selectedTierId}
                                className="modal-button confirm"
                            >
                                {processing ? (
                                    <div className="spinner"></div>
                                ) : (
                                    'Opprett Bruker'
                                )}
                            </button>
                        </div>
                    </>
                ) : (
                    <div className="user-creation-success">
                        <div className="success-icon">
                            <i className="fas fa-check-circle"></i>
                        </div>
                        <h3>Bruker Opprettet!</h3>
                        <div className="user-details">
                            <div className="detail-row">
                                <span className="label">Bruker ID:</span>
                                <span className="value">{userCreationResponse.id}</span>
                            </div>
                            <div className="detail-row">
                                <span className="label">Midlertidig Passord:</span>
                                <span className="value highlight">{userCreationResponse.tempPassword}</span>
                            </div>
                            <div className="detail-row">
                                <span className="label">Abonnement ID:</span>
                                <span className="value">{userCreationResponse.subscriptionId}</span>
                            </div>
                            <div className="detail-row">
                                <span className="label">Kunde ID:</span>
                                <span className="value">{userCreationResponse.customerId}</span>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="modal-button confirm"
                        >
                            Lukk
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default function IndividualUser() {
    const { id } = useParams();
    const { user: authUser, hasPermission, logout } = useAuth();
    const [user, setUser] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState(null);
    const [roles, setRoles] = useState([]);
    const [businesses, setBusinesses] = useState([]);
    const [msg, setMsg] = useState('');
    const [pricingPlans, setPricingPlans] = useState([]);
    const [mailerLiteGroups, setMailerLiteGroups] = useState([]);
    const [selectedMailerLiteGroup, setSelectedMailerLiteGroup] = useState('');
    const [showMailerLiteForm, setShowMailerLiteForm] = useState(false);
    const [userGroups, setUserGroups] = useState([]);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [userSubscription, setUserSubscription] = useState(null);
    const [loadingSubscription, setLoadingSubscription] = useState(false);
    const [selectedInterval, setSelectedInterval] = useState('month');
    const [processingSubscription, setProcessingSubscription] = useState(false);
    const [overlayMessage, setOverlayMessage] = useState('Vennligst vent mens vi omdirigerer deg til vår sikre betalingsside...');
    const [overlayTitle, setOverlayTitle] = useState('Behandler ditt abonnement');
    const [overlayError, setOverlayError] = useState(false);
    
    // New state variables for user creation
    const [showCreateUserModal, setShowCreateUserModal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [selectedTrialDuration, setSelectedTrialDuration] = useState(14);
    const [selectedTierId, setSelectedTierId] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [userCreationResponse, setUserCreationResponse] = useState(null);
    
    // User comments state
    const [userComments, setUserComments] = useState([]);
    const [loadingComments, setLoadingComments] = useState(false);
    const [newComment, setNewComment] = useState('');
    const [editingCommentId, setEditingCommentId] = useState(null);
    const [editingCommentText, setEditingCommentText] = useState('');
    
    const navigate = useNavigate();
    
    const isOwnProfile = authUser?.id === parseInt(id);
    const isAdmin = hasPermission("canManageAllBusinesses");

    // Translation functions
    const translatePlanName = (planName, productId) => {
        // First check product_id if available
        const idToCheck = productId || planName;
        
        if (!idToCheck) return planName || 'Abonnement';
        
        const id = idToCheck.toLowerCase();
        
        // Premium plans - check for full product ID format
        if (id.includes('premium')) {
            if (id.includes('biannual') || id.includes('halvårlig')) {
                return 'Full tilgang halvårlig - Apple abonnement';
            } else if (id.includes('annual') || id.includes('årlig')) {
                return 'Full tilgang årlig - Apple abonnement';
            } else if (id.includes('monthly') || id.includes('månedlig')) {
                return 'Full tilgang månedlig - Apple abonnement';
            }
        }
        
        // Basic plans - check for full product ID format
        if (id.includes('basic')) {
            if (id.includes('annual') || id.includes('årlig')) {
                return 'Av og til årlig - Apple abonnement';
            } else if (id.includes('biannual') || id.includes('halvårlig')) {
                return 'Av og til halvårlig - Apple abonnement';
            } else if (id.includes('monthly') || id.includes('månedlig')) {
                return 'Av og til månedlig - Apple abonnement';
            }
        }
        
        // Return original if no match
        return planName || 'Abonnement';
    };

    const translateStatus = (status) => {
        if (!status) return 'Ukjent';
        
        const statusLower = status.toLowerCase();
        switch (statusLower) {
            case 'active':
                return 'Aktiv';
            case 'canceled':
            case 'cancelled':
                return 'Kansellert';
            case 'trialing':
                return 'Prøveperiode';
            case 'expired':
                return 'Utgått';
            case 'past_due':
                return 'Forfalt';
            case 'unpaid':
                return 'Ubetalt';
            case 'pending':
                return 'Venter';
            default:
                return status.charAt(0).toUpperCase() + status.slice(1);
        }
    };

    useEffect(() => {
        fetchUser();
        
        // Hent bare disse hvis brukeren er admin
        if (hasPermission("canManageAllBusinesses")) {
        fetchRoles();
        fetchBusinesses();
        fetchPricingPlans();
        fetchMailerLiteGroups();
        fetchUserComments();
        } else {
            // For vanlige brukere, hent bare prisplaner for visning
            fetchPricingPlans();
        }
        
        // Hvis man ser på egen profil, hent abonnementsinformasjon
        if (isOwnProfile && hasPermission("canManageOwnSubscription")) {
            fetchUserSubscription();
        }
    }, [id, hasPermission, isOwnProfile]);

    useEffect(() => {
        // Prøv bare å hente brukergrupper hvis admin og vi har brukerens e-post
        if (hasPermission("canManageAllBusinesses") && user?.email) {
            fetchUserGroups();
        }
    }, [user?.email, hasPermission]);

    const fetchUser = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/users/${id}`,
                { withCredentials: true }
            );
            setUser(response.data);
            setEditData(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Feil ved henting av bruker:', error);
            setError(`Kunne ikke laste bruker ${error}`);
            setLoading(false);
        }
    };

    const fetchUserSubscription = async () => {
        try {
            setLoadingSubscription(true);
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/pricing-plans/user`,
                { withCredentials: true }
            );
            
            console.log('API Response:', response.data);
            
            if (response.data && response.data.subscription) {
                // Use the subscription object directly from the new API structure
                console.log('Setting subscription from new structure:', response.data.subscription);
                setUserSubscription(response.data.subscription);
            } else if (response.data.success && response.data.subscriptions && response.data.subscriptions.length > 0) {
                // Fallback to old structure if needed
                const activeSubscription = response.data.subscriptions.find(sub => sub.stripe_status === 'active') 
                    || response.data.subscriptions[0];
                console.log('Setting subscription from old structure:', activeSubscription);
                setUserSubscription(activeSubscription);
            } else {
                console.log('No subscription found in response:', response.data);
                setUserSubscription(null);
            }
            setLoadingSubscription(false);
        } catch (error) {
            console.error('Feil ved henting av brukerabonnement:', error);
            setLoadingSubscription(false);
        }
    };

    const handleCustomerPortal = async () => {
        try {
            setLoadingSubscription(true);
            
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/pricing-plans/create-portal-session`,
                {},
                { withCredentials: true }
            );
            
            if (response.data.url) {
                window.location.href = response.data.url;
            }
        } catch (error) {
            console.error('Feil ved opprettelse av kundeportal:', error);
            if (error.response?.data?.message?.includes('No configuration provided')) {
                setError('Kundeportal er ikke konfigurert. Kontakt support for å administrere ditt abonnement.');
            } else {
                setError('Kunne ikke åpne kundeportal. Prøv igjen senere.');
            }
            setTimeout(() => setError(''), 5000);
        } finally {
            setLoadingSubscription(false);
        }
    };

    const handleSubscriptionAction = async (action) => {
        try {
            setLoadingSubscription(true);
            
            const endpoint = action === 'cancel' ? 'cancel' : 'reactivate';
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/pricing-plans/${endpoint}`,
                { 
                    customerId: userSubscription.customer || userSubscription.subscription_stripe_id // Use customer field or fallback
                },
                { withCredentials: true }
            );
            
            if (response.data.success) {
                // Hent abonnementet på nytt for å få oppdatert data
                await fetchUserSubscription();
                setMsg(`Abonnement ${action === 'cancel' ? 'vil bli kansellert ved slutten av faktureringsperioden' : 'reaktivert'} vellykket`);
                setTimeout(() => setMsg(''), 5000);
            }
        } catch (error) {
            console.error(`Feil ved ${action === 'cancel' ? 'kansellering' : 'reaktivering'} av abonnement:`, error);
            setError(`Kunne ikke ${action === 'cancel' ? 'kansellere' : 'reaktivere'} abonnement: ${error.response?.data?.message || error.message}`);
            setTimeout(() => setError(''), 5000);
        } finally {
            setLoadingSubscription(false);
        }
    };


    const fetchRoles = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/users/roles`,
                { withCredentials: true }
            );
            setRoles(response.data);
        } catch (error) {
            console.error('Feil ved henting av roller:', error);
        }
    };

    const fetchBusinesses = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/businesses`,
                { withCredentials: true }
            );
            setBusinesses(response.data);
        } catch (error) {
            console.error('Feil ved henting av bedrifter:', error);
        }
    };

    // const fetchPricingPlans = async () => {
    //     try {
    //         const response = await axios.get(
    //             `${process.env.REACT_APP_BACKEND_URL}/api/pricing-plans`,
    //             { withCredentials: true }
    //         );
    //         setPricingPlans(response.data);
    //     } catch (error) {
    //         console.error('Feil ved henting av prisplaner:', error);
    //     }
    // };

    const fetchPricingPlans = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/pricing-plans`,
                { withCredentials: true }
            );
            // Filter plans to only include those with tiers
            const plansWithTiers = response.data.filter(plan => plan.tiers && plan.tiers.length > 0);
            setPricingPlans(plansWithTiers);
        } catch (error) {
            console.error('Error fetching pricing plans:', error);
        }
    };

    const fetchMailerLiteGroups = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/mailerlite/groups`,
                { withCredentials: true }
            );
            if (response.data && response.data.data && response.data.data.data) {
            setMailerLiteGroups(response.data.data.data);
            }
        } catch (error) {
            console.error('Feil ved henting av MailerLite-grupper:', error);
            // Ikke sett feilmelding, fortsett uten nyhetsbrevgrupper
        }
    };

    const fetchUserGroups = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/mailerlite/subscribers/${encodeURIComponent(user.email)}/groups`,
                { withCredentials: true }
            );
            if (response.data && response.data.data) {
            setUserGroups(response.data.data);
            }
        } catch (error) {
            console.error('Feil ved henting av brukergrupper:', error);
            // Ikke sett feil, fortsett uten grupper
        }
    };

    // User comments functions
    const fetchUserComments = async () => {
        if (!isAdmin || !id) return;
        
        try {
            setLoadingComments(true);
            const response = await api.get(`/api/users/${id}/comments`);
            if (response.data.success) {
                setUserComments(response.data.comments);
            }
        } catch (error) {
            console.error('Feil ved henting av kommentarer:', error);
        } finally {
            setLoadingComments(false);
        }
    };

    const handleCreateComment = async () => {
        if (!newComment.trim()) {
            setMsg('Kommentar kan ikke være tom');
            return;
        }

        try {
            setProcessing(true);
            const response = await api.post(`/api/users/${id}/comments`, { comment: newComment });
            
            if (response.data.success) {
                setMsg('Kommentar opprettet');
                setNewComment('');
                await fetchUserComments();
                setTimeout(() => setMsg(''), 3000);
            }
        } catch (error) {
            console.error('Feil ved opprettelse av kommentar:', error);
            setError(error.response?.data?.message || 'Kunne ikke opprette kommentar');
            setTimeout(() => setError(''), 5000);
        } finally {
            setProcessing(false);
        }
    };

    const handleUpdateComment = async (commentId) => {
        if (!editingCommentText.trim()) {
            setMsg('Kommentar kan ikke være tom');
            return;
        }

        try {
            setProcessing(true);
            const response = await api.put(`/api/users/comments/${commentId}`, { comment: editingCommentText });
            
            if (response.data.success) {
                setMsg('Kommentar oppdatert');
                setEditingCommentId(null);
                setEditingCommentText('');
                await fetchUserComments();
                setTimeout(() => setMsg(''), 3000);
            }
        } catch (error) {
            console.error('Feil ved oppdatering av kommentar:', error);
            setError(error.response?.data?.message || 'Kunne ikke oppdatere kommentar');
            setTimeout(() => setError(''), 5000);
        } finally {
            setProcessing(false);
        }
    };

    const handleDeleteComment = async (commentId) => {
        if (!window.confirm('Er du sikker på at du vil slette denne kommentaren?')) {
            return;
        }

        try {
            setProcessing(true);
            const response = await api.delete(`/api/users/comments/${commentId}`);
            
            if (response.data.success) {
                setMsg('Kommentar slettet');
                await fetchUserComments();
                setTimeout(() => setMsg(''), 3000);
            }
        } catch (error) {
            console.error('Feil ved sletting av kommentar:', error);
            setError(error.response?.data?.message || 'Kunne ikke slette kommentar');
            setTimeout(() => setError(''), 5000);
        } finally {
            setProcessing(false);
        }
    };

    const handleEdit = () => {
        setEditData({
            ...user,
            pricing_id: user.pricing_id || null
        });
        setIsEditing(true);
    };

    const handleDelete = async (id) => {
        try {
            const response = await axios.delete(
                `${process.env.REACT_APP_BACKEND_URL}/api/users/${id}`,
                { withCredentials: true }
            );
            if (response.status === 200) {
                setDeleteConfirm(null);
                navigate('/users');
            }
        } catch (error) {
            console.error('Feil ved sletting av bruker:', error);
        }
   
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditData({
            ...user,
            pricing_id: user.pricing_id || null
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setEditData(prev => ({
            ...prev,
            [name]: value === "null" ? null : value,
            ...(name === 'businessId' && {
                businessId: value,
                businessName: value ? businesses.find(b => b.idbusiness === parseInt(value))?.name : '',
                pricing_id: value ? null : prev.pricing_id
            }),
            ...(name === 'phone_number' && {
                phone_number: value
            })
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            // Check if a pricing plan is selected
            if (editData.pricing_id && editData.pricing_id !== "null" && !editData.businessId) {
                // Parse the pricing plan and tier IDs
                const [pricingPlanId, tierId] = editData.pricing_id.split('-');
                
                if (pricingPlanId && tierId) {
                    // Create subscription for the user with trial duration
                    const trialDuration = editData.trialDuration || 14;
                    await handleCreateSubscriptionForUser(pricingPlanId, tierId, trialDuration);
                    return; // Exit early since handleCreateSubscriptionForUser handles the rest
                }
            }

            // Regular user update (no pricing plan selected)
            const [firstName, lastName] = editData.name.split(' ');
            const response = await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/users/${id}`,
                {
                    firstName,
                    lastName,
                    email: editData.email,
                    role: editData.role,
                    phone: editData.phone_number,
                    businessId: editData.businessId || null,
                    pricingId: (!editData.businessId && editData.pricing_id !== "null") 
                        ? editData.pricing_id 
                        : null
                },
                { withCredentials: true }
            );

            if (response.status === 200) {
                setMsg('Bruker oppdatert');
                if (response.data.checkoutUrl) {
                    if (response.data.clientSecret) {
                        localStorage.setItem('stripe_client_secret', response.data.clientSecret);
                    }
                    window.location.href = response.data.checkoutUrl;
                } else {
                    setIsEditing(false);
                    fetchUser();
                    setTimeout(() => setMsg(''), 3000);
                }
            }
        } catch (error) {
            console.error('Error updating user:', error);
            setError(error.response?.data?.message || 'Kunne ikke oppdatere bruker');
            setTimeout(() => setError(''), 3000);
        }
    };

    const assignToMailerLiteGroup = async (e) => {
        e.preventDefault();
        try {
            await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/mailerlite/subscribers`,
                {
                    email: editData.email,
                    name: editData.name,
                    groupId: selectedMailerLiteGroup
                },
                { withCredentials: true }
            );
            setMsg('Bruker lagt til i nyhetsbrevgruppe');
            setShowMailerLiteForm(false);
            setSelectedMailerLiteGroup('');
        } catch (error) {
            console.error('Feil ved tillegging i nyhetsbrevgruppe:', error);
            setError('Kunne ikke legge til bruker i nyhetsbrevgruppe');
        }
    };

    const removeSubscriber = async (email, groupId) => {
        if (window.confirm('Er du sikker på at du vil fjerne denne brukeren fra gruppen?')) {
            try {
                await axios.delete(
                    `${process.env.REACT_APP_BACKEND_URL}/api/mailerlite/groups/${groupId}/subscribers/${email}`,
                    { withCredentials: true }
                );
                setMsg('Bruker fjernet fra gruppe');
                fetchUserGroups(); // Oppdater gruppelisten
            } catch (error) {
                setError('Kunne ikke fjerne bruker fra gruppe');
                console.error('Feil ved fjerning fra gruppe:', error);
            }
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        
        let date;
        // Check if it's a Unix timestamp (number) or ISO string
        if (typeof dateString === 'number') {
            // Unix timestamp - multiply by 1000 to convert to milliseconds
            date = new Date(dateString * 1000);
        } else {
            // ISO string or other format
            date = new Date(dateString);
        }
        
        return date.toLocaleDateString('nb-NO', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const handleSubscribeNow = async (plan, tier) => {
        try {
            setProcessingSubscription(true);
            setOverlayError(false);
            setOverlayTitle('Behandler ditt abonnement');
            setOverlayMessage('Vennligst vent mens vi omdirigerer deg til vår sikre betalingsside...');
            setError('');
            
            // Sjekk at vi har tier ID og stripe_price_id
            if (!tier || !tier.id) {
                setError('Ugyldig abonnementsnivå valgt.');
                setOverlayError(true);
                setOverlayTitle('Abonnementsfeil');
                setOverlayMessage('Ugyldig abonnementsnivå valgt. Vennligst prøv igjen.');
                setTimeout(() => setProcessingSubscription(false), 2000);
                return;
            }
            
            console.log('Abonnerer på nivå:', tier.id, 'for plan:', plan.name);
            console.log('Bruker Stripe pris-ID:', tier.stripe_price_id);
            
            setOverlayMessage('Oppretter ditt abonnement...');
            
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/pricing-plans/create`,
                {
                    priceId: tier.id,
                    userId: authUser.id
                },
                { withCredentials: true }
            );
            
            if (response.data.success && response.data.checkoutUrl) {
                console.log('Omdirigerer til Stripe betaling:', response.data.checkoutUrl);
                setOverlayMessage('Omdirigerer til Stripe for sikker betaling...');
                // Hvis vi har en checkout URL, omdiriger til Stripe checkout
                setTimeout(() => {
                    window.location.href = response.data.checkoutUrl;
                }, 1000);
            } else if (response.data.success) {
                setMsg('Abonnement opprettet');
                fetchUserSubscription();
                setProcessingSubscription(false);
            } else {
                setError('Feil ved opprettelse av abonnement: Ingen checkout URL mottatt');
                setOverlayError(true);
                setOverlayTitle('Abonnementsfeil');
                setOverlayMessage('Kunne ikke opprette abonnement. Vennligst prøv igjen senere.');
                setTimeout(() => setProcessingSubscription(false), 2000);
            }
        } catch (error) {
            console.error('Feil ved opprettelse av abonnement:', error);
            const errorMessage = error.response?.data?.message || 'Kunne ikke opprette abonnement';
            setError(errorMessage);
            setOverlayError(true);
            setOverlayTitle('Abonnementsfeil');
            setOverlayMessage(errorMessage);
            setTimeout(() => setProcessingSubscription(false), 2000);
        }
    };

    // User creation functions
    const handleCreateUser = () => {
        // Show the modal with default values
        setSelectedPlan(null);
        setSelectedTrialDuration(14);
        setSelectedTierId(null);
        setUserCreationResponse(null);
        setShowCreateUserModal(true);
    };

    const handleConfirmCreateUser = async () => {
        if (!selectedPlan || !selectedTrialDuration || !selectedTierId) {
            setMsg('Vennligst velg en plan og prøveperiode');
            return;
        }

        try {
            setProcessing(true);
            setMsg('Oppretter bruker...');

            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/users/create-from-subscriber`,
                {
                    name: user.name,
                    email: user.email,
                    phone: user.phone_number || '',
                    trialDuration: selectedTrialDuration,
                    pricingPlanId: selectedPlan,
                    tierId: selectedTierId
                },
                { withCredentials: true }
            );

            if (response.status === 201) {
                setUserCreationResponse(response.data);
                setMsg('Bruker opprettet');
                // Refresh user data to show subscription
                fetchUser();
                fetchUserSubscription();
            }
        } catch (error) {
            setMsg(error.response?.data?.message || 'Kunne ikke opprette bruker');
        } finally {
            setProcessing(false);
        }
    };

    const handleCloseCreateUserModal = () => {
        setShowCreateUserModal(false);
        setSelectedPlan(null);
        setSelectedTrialDuration(14);
        setSelectedTierId(null);
        setUserCreationResponse(null);
    };

    // Function to create subscription for existing user
    const handleCreateSubscriptionForUser = async (pricingPlanId, tierId, trialDuration = 14) => {
        try {
            setProcessing(true);
            setMsg('Oppretter abonnement for bruker...');

            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/users/create-subscription-for-user`,
                {
                    userId: user.idusers,
                    name: user.name,
                    email: user.email,
                    phone: user.phone_number || '',
                    trialDuration: trialDuration,
                    pricingPlanId: pricingPlanId,
                    tierId: tierId
                },
                { withCredentials: true }
            );

            if (response.status === 201) {
                setMsg('Abonnement opprettet for bruker');
                // Refresh user data to show subscription
                fetchUser();
                fetchUserSubscription();
                setIsEditing(false);
            }
        } catch (error) {
            console.error('Error creating subscription for user:', error);
            setError(error.response?.data?.message || 'Kunne ikke opprette abonnement for bruker');
            setTimeout(() => setError(''), 5000);
        } finally {
            setProcessing(false);
        }
    };

    // Function to send reset password link
    const handleSendResetPassword = async () => {
        try {
            setProcessing(true);
            setMsg('Sender tilbakestillingslenke...');

            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/request-password-reset`,
                { email: user.email },
                { withCredentials: true }
            );

            if (response.status === 200) {
                setMsg('Tilbakestillingslenke sendt til bruker');
                setTimeout(() => setMsg(''), 5000);
            }
        } catch (error) {
            console.error('Error sending reset password link:', error);
            setError(error.response?.data?.message || 'Kunne ikke sende tilbakestillingslenke');
            setTimeout(() => setError(''), 5000);
        } finally {
            setProcessing(false);
        }
    };

    const renderSubscriptionSection = () => {
        if (loadingSubscription) {
            return <div className="loading-indicator">Laster abonnementsdata...</div>;
        }

        console.log(userSubscription, "USER SUBSCRIPTION");
        console.log('Subscription check:', {
            hasUserSubscription: !!userSubscription,
            hasStatus: !!userSubscription?.status,
            hasStripeStatus: !!userSubscription?.stripe_status,
            status: userSubscription?.status,
            stripeStatus: userSubscription?.stripe_status
        });

        // Check if userSubscription has actual subscription data
        if (!userSubscription || (!userSubscription.status && !userSubscription.stripe_status)) {
            return (
                <div className="no-subscription">
                    <h3>Ingen aktivt abonnement</h3>
                    <p>Du har ikke et aktivt abonnement med Stripe.</p>
                    <div className="subscription-actions">
                        <button 
                            className="upgrade-button"
                            onClick={handleCustomerPortal}
                            disabled={loadingSubscription}
                        >
                            <i className="fas fa-arrow-circle-up"></i> Få et abonnement
                        </button>
                        {isAdmin && (
                            <button 
                                className="create-user-button"
                                onClick={handleCreateUser}
                                disabled={processing}
                            >
                                <i className="fas fa-user-plus"></i> Opprett Bruker med Abonnement
                            </button>
                        )}
                    </div>

                    <h3>Dine gamle abonnementer</h3>
                {user.subscriptions && user.subscriptions.length > 0 ? (
                    <div className="old-subscriptions-list">
                        {user.subscriptions.map((subscription, idx) => (
                            <div
                                key={subscription.id || idx}
                                className="old-subscription-row"
                                style={{
                                    border: "1px solid #eee",
                                    borderRadius: "6px",
                                    padding: "12px",
                                    marginBottom: "12px",
                                    width: "100%",
                                    boxSizing: "border-box",
                                    background: "#fafafa"
                                }}
                            >
                                <div style={{display: "flex", flexWrap: "wrap", width: "100%"}}>
                                    <div style={{flex: "1 1 250px", minWidth: "200px", marginBottom: "8px"}}>
                                        <strong>Plan:</strong> {translatePlanName(subscription.pricing?.name || subscription.plan_name, subscription.product_id)}
                                    </div>
                                    <div style={{flex: "1 1 120px", minWidth: "140px", marginBottom: "8px"}}>
                                        <strong>Status:</strong> {translateStatus(subscription.status)}
                                    </div>
                                    <div style={{flex: "1 1 200px", minWidth: "180px", marginBottom: "8px"}}>
                                        <strong>Prøveperiode utløper:</strong>{" "}
                                        {subscription.trial_end
                                            ? new Date(
                                                subscription.trial_end * 1000
                                              ).toLocaleDateString("no-NO", {
                                                year: "numeric",
                                                month: "long",
                                                day: "numeric",
                                              })
                                            : <span style={{color:"#888"}}>Ingen</span>
                                        }
                                    </div>
                                    <div style={{flex: "1 1 250px", minWidth: "180px", marginBottom: "8px"}}>
                                        <strong>Stripe ID:</strong>{" "}
                                        {subscription.stripe_id
                                          ? <span style={{fontFamily:"monospace"}}>{subscription.stripe_id}</span>
                                          : <span style={{color:"#888"}}>Ingen</span>
                                        }
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="no-old-subscriptions">Ingen tidligere abonnementer funnet.</div>
                )}
                </div>
            );
        } else {

        return (
            <div className="subscription-card">
                <div className="subscription-header">
                    <h3>
                        <i className="fas fa-crown"></i> 
                        {translatePlanName(userSubscription.plan?.name || userSubscription.plan_name, userSubscription.product_id)}
                    </h3>
                    <span className={`status-badge ${userSubscription.status || userSubscription.stripe_status || 'inactive'} ${userSubscription.cancel_at_period_end ? 'canceling' : ''}`}>
                        {userSubscription.cancel_at_period_end ? 'Kansellerer' :
                         translateStatus(userSubscription.status || userSubscription.stripe_status)}
                    </span>
                </div>

                <div className="subscription-details">
                    <div className="detail-row">
                        <span className="detail-label">Plan:</span>
                        <span className="detail-value">{translatePlanName(userSubscription.plan?.name || userSubscription.plan_name, userSubscription.product_id)}</span>
                    </div>
                    <div className="detail-row">
                        <span className="detail-label">Pris betalt:</span>
                        <span className="detail-value">
                            {userSubscription.plan?.amount ? 
                                `${(userSubscription.plan.amount / 100).toFixed(2)} ${userSubscription.currency?.toUpperCase() || 'NOK'}` :
                                userSubscription.amount_paid ? 
                                    `${userSubscription.amount_paid} ${(userSubscription.currency || userSubscription.plan_currency)?.toUpperCase()}` :
                                    `${userSubscription.tier_amount} ${(userSubscription.plan_currency)?.toUpperCase()}`
                            }
                        </span>
                    </div>
                    <div className="detail-row">
                        <span className="detail-label">Faktureringssyklus:</span>
                        <span className="detail-value">
                            {userSubscription.plan?.interval_count ? 
                                `${userSubscription.plan.interval_count} ${userSubscription.plan.interval}${userSubscription.plan.interval_count > 1 ? 's' : ''}` :
                                userSubscription.tier_interval === 'month' 
                                    ? `${userSubscription.tier_interval_count || 1} Måned${(userSubscription.tier_interval_count > 1) ? 'er' : ''}` 
                                    : `Årlig`
                            }
                        </span>
                    </div>
                    {userSubscription.current_period_start && userSubscription.current_period_end && (
                        <div className="detail-row">
                            <span className="detail-label">Nåværende periode:</span>
                            <span className="detail-value">
                                {formatDate(userSubscription.current_period_start)} til {formatDate(userSubscription.current_period_end)}
                            </span>
                        </div>
                    )}
                    {userSubscription.trial_end && (
                        <div className="detail-row">
                            <span className="detail-label">Prøveperiode slutter:</span>
                            <span className="detail-value">{formatDate(userSubscription.trial_end)}</span>
                        </div>
                    )}
                    {userSubscription.cancel_at_period_end && (
                        <div className="detail-row">
                            <span className="detail-label">Kansellering:</span>
                            <span className="detail-value cancel-notice">
                                Ditt abonnement vil avsluttes {formatDate(userSubscription.current_period_end)}
                            </span>
                        </div>
                    )}
                </div>

                <div className="subscription-actions">
                    {(
                        (
                            userSubscription.status === 'active' ||
                            userSubscription.status === 'trialing' ||
                            userSubscription.stripe_status === 'active' ||
                            userSubscription.stripe_status === 'trialing'
                        )
                        && userSubscription.subscription_type !== 'ios'
                    ) && (
                        <>
                            <button 
                                className="upgrade-button"
                                onClick={handleCustomerPortal}
                                disabled={loadingSubscription}
                            >
                                <i className="fas fa-arrow-circle-up"></i> Oppgrader plan
                            </button>
                            
                            <button 
                                className="upgrade-button"
                                onClick={handleCustomerPortal}
                                disabled={loadingSubscription}
                            >
                                <i className="fas fa-cog"></i> Administrer fakturering
                            </button>
                            
                            {isAdmin && (
                                <button 
                                    className="reset-password-button"
                                    onClick={handleSendResetPassword}
                                    disabled={processing}
                                >
                                    <i className="fas fa-key"></i> Send tilbakestillingslenke
                                </button>
                            )}
                            
                            {userSubscription.cancel_at_period_end ? (
                                <button 
                                    className="reactivate-button"
                                    onClick={() => handleSubscriptionAction('reactivate')}
                                    disabled={loadingSubscription}
                                >
                                    <i className="fas fa-sync"></i> Reaktiver
                                </button>
                            ) : (
                                <button 
                                    className="cancel-button"
                                    onClick={handleCustomerPortal}
                                    disabled={loadingSubscription}
                                >
                                    <i className="fas fa-times-circle"></i> Kanseller
                                </button>
                            )}
                        </>
                    )}

                    {/* userSubscription.subscription_type !== 'ios' */}
                    {userSubscription.subscription_type === 'ios' && (
                        <div className="subscription-actions">
                       <p>Du har et Apple abonnement. Du kan ikke endre eller kansellere abonnementet her, gjør det i appen.</p>
                        </div>
                    )}
                    {(userSubscription.status === 'canceled' || userSubscription.stripe_status === 'canceled') && (
                        <>
                            <button 
                                className="upgrade-button full-width"
                                onClick={handleCustomerPortal}
                                disabled={loadingSubscription}
                            >
                                <i className="fas fa-arrow-circle-up"></i> Få et nytt abonnement
                            </button>
                            <button 
                                className="cancel-button"
                                onClick={handleCustomerPortal}
                                disabled={loadingSubscription}
                            >
                                <i className="fas fa-cog"></i> Administrer abonnement
                            </button>
                            {isAdmin && (
                                <button 
                                    className="reset-password-button"
                                    onClick={handleSendResetPassword}
                                    disabled={processing}
                                >
                                    <i className="fas fa-key"></i> Send tilbakestillingslenke
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
        );
    }
    };

    const renderPricingTiers = () => {
        // Filtrer planer som har tiers_mode = 1
        const tieredPlans = pricingPlans.filter(plan => plan.tiers_mode === 1);
        
        if (tieredPlans.length === 0) {
            return <p className="no-plans">Ingen nivåbaserte prisplaner tilgjengelig</p>;
        }

        return (
            <div className="pricing-section">
                                <div className="interval-selector">
                    <button 
                        className={`interval-button ${selectedInterval === 'month' ? 'active' : ''}`}
                        onClick={() => setSelectedInterval('month')}
                    >
                        Månedlig
                    </button>
                    <button 
                        className={`interval-button ${selectedInterval === 'month-6' ? 'active' : ''}`}
                        onClick={() => setSelectedInterval('month-6')}
                    >
                        6 måneder
                    </button>
                    <button 
                        className={`interval-button ${selectedInterval === 'year' ? 'active' : ''}`}
                        onClick={() => setSelectedInterval('year')}
                    >
                        Årlig
                    </button>
                </div>
                
                <div className="pricing-cards-dash">
                    {tieredPlans.map(plan => {
                        // Finn nivået som matcher valgt intervall
                        let selectedTier;
                        if (selectedInterval === 'month') {
                            selectedTier = plan.tiers.find(tier => 
                                tier.payment_interval === 'month' && tier.interval_count === 1
                            );
                        } else if (selectedInterval === 'month-6') {
                            selectedTier = plan.tiers.find(tier => 
                                tier.payment_interval === 'month' && tier.interval_count === 6
                            );
                        } else if (selectedInterval === 'year') {
                            selectedTier = plan.tiers.find(tier => 
                                tier.payment_interval === 'year' && tier.interval_count === 1
                            );
                        }
                        
                        // Hopp over hvis ingen matchende nivå
                        if (!selectedTier) return null;
                        
                        // Beregn månedlig ekvivalent pris for 6-måneders og årlige planer
                        const monthlyPrice = selectedInterval === 'month' 
                            ? selectedTier.amount
                            : selectedInterval === 'month-6'
                                ? (parseFloat(selectedTier.amount) / 6).toFixed(2)
                                : (parseFloat(selectedTier.amount) / 12).toFixed(2);
                        
                        // Beregn besparelser sammenlignet med månedlig plan
                        const monthlySaving = () => {
                            const monthlyTier = plan.tiers.find(tier => 
                                tier.payment_interval === 'month' && tier.interval_count === 1
                            );
                            
                            if (!monthlyTier || selectedInterval === 'month') return null;
                            
                            const regularMonthlyPrice = parseFloat(monthlyTier.amount);
                            const currentMonthlyPrice = parseFloat(monthlyPrice);
                            
                            if (regularMonthlyPrice <= 0 || currentMonthlyPrice <= 0) return null;
                            
                            const savingsPercentage = Math.round(
                                ((regularMonthlyPrice - currentMonthlyPrice) / regularMonthlyPrice) * 100
                            );
                            
                            return savingsPercentage > 0 ? savingsPercentage : null;
                        };
                        
                        const savingsPercentage = monthlySaving();
                        
                        return (
                            <div key={plan.idpricing} className={`pricing-card-dash ${plan.name.toLowerCase().includes('full tilgang') ? 'popular' : ''}`}>
                                {/* Badges */}
                                {plan.name.toLowerCase().includes('full tilgang') && (
                                    <div className="popular-badge">Best for nybegynnere</div>
                                )}
                                {selectedInterval === 'year' && (
                                    <div className="best-value-badge">Beste verdi</div>
                                )}
                                {savingsPercentage && (
                                    <div className="savings-badge">
                                        Spar {savingsPercentage}%
                                    </div>
                                )}
                                
                                {/* Header */}
                                <div className="pricing-header">
                                    <h3>{plan.name}</h3>
                                </div>
                                
                                {/* Price */}
                                <div className="pricing-price">
                                    <span className="currency">NOK</span>
                                    <span className="amount">{selectedTier.amount}</span>
                                    <span className="period">
                                        /{selectedInterval === 'month' ? 'mnd' : selectedInterval === 'month-6' ? '6 mnd' : 'år'}
                                    </span>
                                </div>
                                
                                {/* Monthly equivalent */}
                                {selectedInterval !== 'month' && (
                                    <div className="monthly-equivalent">
                                        Kun NOK {monthlyPrice}/mnd
                                    </div>
                                )}
                                
                                {/* Description */}
                                <div className="pricing-description">
                                    {plan.description || `${plan.name} abonnementsplan`}
                                </div>
                                
                                {/* Features */}
                                <div className="pricing-features">
                                    <ul>
                                        <li><i className="fas fa-check"></i> Ubegrenset tilgang til alle økter</li>
                                        <li><i className="fas fa-check"></i> Lage egne spillelister</li>
                                        <li><i className="fas fa-check"></i> Dine favoritter</li>
                                        <li><i className="fas fa-check"></i> Finn ditt nivå</li>
                                        {plan.name.toLowerCase().includes('full tilgang') && (
                                            <>
                                                <li className="highlighted-feature">
                                                    <i className="fas fa-star"></i> 
                                                    <span className="feature-text">
                                                        <strong>Alle treningsprogram</strong>
                                                        <span className="feature-description">Komplett treningsplan for nybegynnere</span>
                                                    </span>
                                                </li>
                                            </>
                                        )}
                                    </ul>
                                </div>
                                
                                {/* Action Button */}
                                <div className="pricing-actions">
                                    <button 
                                        className={`select-plan-button ${plan.name.toLowerCase().includes('full tilgang') ? 'popular-button' : ''}`}
                                        onClick={() => 
                                            isAdmin 
                                                ? handleChange({target: {name: 'pricing_id', value: plan.idpricing}})
                                                : handleSubscribeNow(plan, selectedTier)
                                        }
                                        disabled={processingSubscription}
                                    >
                                        {isAdmin 
                                            ? 'Velg plan' 
                                            : processingSubscription 
                                                ? 'Behandler...' 
                                                : 'Abonner nå'
                                        }
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
                
                {error && (
                    <div className="subscription-error">
                        <p><i className="fas fa-exclamation-circle"></i> {error}</p>
                    </div>
                )}
            </div>
        );
    };

    if (loading) return <div className="loading">Laster...</div>;
    if (error) return <div className="error-message">{error}</div>;
    if (!user) return <div className="error-message">Bruker ikke funnet</div>;

    return (
        <div className="page-container">
            {processingSubscription && (
                <div className="redirect-overlay">
                    <div className="redirect-modal">
                        {overlayError ? (
                            <div className="redirect-error">
                                <i className="fas fa-exclamation-circle"></i>
                            </div>
                        ) : (
                            <div className="redirect-spinner"></div>
                        )}
                        <h3>{overlayTitle}</h3>
                        <p>{overlayMessage}</p>
                    </div>
                </div>
            )}
            
            <div className="page-header-stack">
                {isAdmin ? (
                <Link to="/users" className="back-button">
                    <i className="fas fa-arrow-left"></i> Tilbake til Brukere
                </Link>
                ) : (
                    <Link to="https://lopeglad.no" className="back-button">
                        <i className="fas fa-arrow-left"></i> Tilbake til Løpeglad
                    </Link>
                )}
                <h1>{isOwnProfile ? 'Min profil' : 'Brukerdetaljer'}</h1>
                {!isAdmin && (
                    <div className="logout-button-container">
                        <button
                            onClick={logout}
                            className="logout-button"
                        >
                            <i className="fas fa-sign-out-alt"></i> Logg ut
                        </button>
                    </div>
                )}
            </div>

            {msg && <div className="success-message">{msg}</div>}

            <div className="detail-card">
                {isEditing ? (
                    <form onSubmit={handleSubmit} className="edit-form">
                        <div className="form-group">
                            <label>Navn:</label>
                            <input
                                type="text"
                                name="name"
                                value={editData.name}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>E-post:</label>
                            <input
                                type="email"
                                name="email"
                                value={editData.email}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Telefonnummer:</label>
                            <input
                                type="tel"
                                name="phone_number"
                                value={editData.phone_number || ''}
                                onChange={handleChange}
                                placeholder="+47 XXX XX XXX"
                            />
                        </div>

                        {isAdmin && (
                            <>
                        <div className="form-group">
                            <label>Rolle:</label>
                            <select
                                name="role"
                                value={editData.role}
                                onChange={handleChange}
                                required
                            >
                                {roles.map(role => (
                                    <option key={role.idrole} value={role.role}>
                                        {role.role}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Bedrift:</label>
                            <select
                                name="businessId"
                                value={editData.businessId || ''}
                                onChange={handleChange}
                            >
                                <option value="">No business</option>
                                {businesses.map(business => (
                                    <option 
                                        key={business.idbusiness} 
                                        value={business.idbusiness}
                                    >
                                        {business.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {!editData.businessId && (
                            <div className="form-group">
                                <label>Pricing Plan:</label>
                                
                                {/* Display current subscriptions info */}
                                {user.subscriptions && user.subscriptions.length > 0 && (
                                    <div className="current-subscription-info">
                                        <p><strong>Nåværende abonnementer ({user.subscriptions.length}):</strong></p>
                                        {user.subscriptions.map((subscription, index) => {
                                            const currentPlan = pricingPlans.find(plan => plan.idpricing === subscription.pricing.id);
                                            const currentTier = currentPlan?.tiers?.find(tier => tier.id === subscription.tierId);
                                            
                                            return (
                                                <div key={subscription.id} className="subscription-item">
                                                    <div className="subscription-header">
                                                        <h4>{translatePlanName(subscription.pricing?.name, subscription.product_id)}</h4>
                                                        <span className={`status-tag ${subscription.status}`}>
                                                            {translateStatus(subscription.status)}
                                                        </span>
                                                    </div>
                                                    {currentPlan && currentTier ? (
                                                        <div className="subscription-details">
                                                            <p><strong>Nivå:</strong> {currentTier.name}</p>
                                                            <p><strong>Pris:</strong> {currentTier.amount} NOK</p>
                                                            <p><strong>Fakturering:</strong> {currentTier.interval_count} {currentTier.payment_interval}
                                                                {currentTier.interval_count > 1 ? 's' : ''}</p>
                                                            <p><strong>Abonnement ID:</strong> {subscription.id}</p>
                                                            {subscription.stripeId && (
                                                                <p><strong>Stripe ID:</strong> <span className="stripe-id">{subscription.stripeId}</span></p>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div className="subscription-details">
                                                            <p><strong>Abonnement ID:</strong> {subscription.id}</p>
                                                            {subscription.stripeId && (
                                                                <p><strong>Stripe ID:</strong> <span className="stripe-id">{subscription.stripeId}</span></p>
                                                            )}
                                                            <p><em>Detaljert nivåinformasjon ikke tilgjengelig</em></p>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                
                                <select
                                    name="pricing_id"
                                    value={editData.pricing_id === null ? "null" : (editData.pricing_id || '')}
                                    onChange={handleChange}
                                    className="form-select"
                                >
                                    <option value="">Velg en prisplan</option>
                                    <option value="null">Gratis plan</option>
                                    {pricingPlans.map(plan => (
                                            plan.tiers_mode === 1 && plan.tiers && plan.tiers.length > 0 ? (
                                                plan.tiers.map(tier => (
                                                    <option 
                                                        key={`${plan.idpricing}-${tier.id}`} 
                                                        value={`${plan.idpricing}-${tier.id}`}
                                                    >
                                                        {plan.name} - {tier.amount} NOK - {tier.interval_count} {tier.payment_interval}
                                                        {tier.interval_count > 1 ? 's' : ''}
                                                    </option>
                                                ))
                                            ) : null
                                        ))}
                                </select>
                                
                                {/* Show trial duration selector when a pricing plan is selected */}
                                {editData.pricing_id && editData.pricing_id !== "null" && (
                                    <div className="trial-duration-section">
                                        <label>Prøveperiode: {editData.trialDuration || 14} dager</label>
                                        <input
                                            type="range"
                                            min="1"
                                            max="365"
                                            value={editData.trialDuration || 14}
                                            onChange={(e) => setEditData(prev => ({
                                                ...prev,
                                                trialDuration: Number(e.target.value)
                                            }))}
                                            className="trial-slider"
                                        />
                                        <div className="trial-duration-markers">
                                            <span>1 dag</span>
                                            <span>180 dager</span>
                                            <span>365 dager</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="newsletter-section">
                            <h3>Nyhetsbrev administrasjon</h3>
                            {!showMailerLiteForm ? (
                                <button
                                    type="button"
                                    className="secondary-button"
                                    onClick={() => setShowMailerLiteForm(true)}
                                >
                                    <i className="fas fa-envelope"></i> Tildel til nyhetsbrev gruppe
                                </button>
                            ) : (
                                <div className="newsletter-form">
                                    <div className="form-group">
                                        <label>Velg nyhetsbrev gruppe:</label>
                                        <select
                                            value={selectedMailerLiteGroup}
                                            onChange={(e) => setSelectedMailerLiteGroup(e.target.value)}
                                        >
                                            <option value="">Velg en gruppe</option>
                                            {mailerLiteGroups.map(group => (
                                                <option key={group.id} value={group.id}>
                                                    {group.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-actions">
                                        <button
                                            type="button"
                                            className="save-button"
                                            onClick={assignToMailerLiteGroup}
                                        >
                                            Tildel til gruppe
                                        </button>
                                        <button
                                            type="button"
                                            className="cancel-button"
                                            onClick={() => {
                                                setShowMailerLiteForm(false);
                                                setSelectedMailerLiteGroup('');
                                            }}
                                        >
                                            Avbryt
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                            </>
                        )}

                        <div className="form-actions">
                            <button type="submit" className="save-button">
                                Lagre endringer
                            </button>
                            <button 
                                type="button" 
                                className="cancel-button"
                                onClick={handleCancel}
                            >
                                Avbryt
                            </button>
                        </div>
                    </form>
                ) : (
                    <>
                        <div className="detail-header">
                            <h2>{user.name}</h2>
                            <span className="role-badge">{user.role}</span>
                        </div>
                        
                        <div className="detail-content">
                            <div className="detail-row">
                                <label>Epost:</label>
                                <span>{user.email}</span>
                            </div>
                            
                            {user.phone_number && (
                                <div className="detail-row">
                                    <label>Telefonnummer:</label>
                                    <span>{user.phone_number}</span>
                                </div>
                            )}
                            
                            {user.businessName && (
                                <div className="detail-row">
                                    <label>Bedrift:</label>
                                    <span>{user.businessName}</span>
                                </div>
                            )}
                            
                            {!user.businessName && (
                                <div className="detail-row">
                                    <label>Priser:</label>
                                    <span>
                                        {user.subscriptions && user.subscriptions.length > 0 
                                            ? `${user.subscriptions.length} abonnement${user.subscriptions.length > 1 ? 'er' : ''}`
                                            : 'Gratis plan'
                                        }
                                    </span>
                                </div>
                            )}
                            
                            <div className="detail-row">
                                <label>Opprettet:</label>
                                <span>{new Date(user.created_at).toLocaleDateString()}</span>
                            </div>
                            <div className="detail-row">
                                <label>Enhet:</label>
                                <span>{user.device}</span>
                            </div>
                            
                            {isAdmin && user.stripe_customer && (
                                <div className="detail-row">
                                    <label>Stripe kunde ID:</label>
                                    <span className="stripe-id">{user.stripe_customer}</span>
                                </div>
                            )}
                            
                            {isAdmin && user.subscriptions && user.subscriptions.length > 0 && (
                                <div className="detail-row">
                                    <label>Abonnementer:</label>
                                    <div className="subscriptions-accordion">
                                        {user.subscriptions.map((subscription, index) => (
                                            <div key={subscription.id} className="subscription-accordion-item">
                                                <button 
                                                    className="accordion-header"
                                                    onClick={() => {
                                                        const accordionItem = document.querySelector(`[data-subscription-id="${subscription.id}"]`);
                                                        accordionItem.classList.toggle('active');
                                                    }}
                                                >
                                                    <span className="subscription-title">
                                                        {translatePlanName(subscription.pricing?.name, subscription.product_id)}
                                                    </span>
                                              
                                                    <div className="accordion-right">
                                                        <span className={`status-tag ${subscription.status}`}>
                                                            {translateStatus(subscription.status)}
                                                        </span>
                                                        <i className="fas fa-chevron-down accordion-icon"></i>
                                                    </div>
                                                </button>
                                                <div className="accordion-content" data-subscription-id={subscription.id}>
                                                    <div className="subscription-details">
                                                        <div className="detail-item">
                                                            <strong>Abonnement ID:</strong> {subscription.id}
                                                        </div>
                                                        {subscription.stripeId && (
                                                            <div className="detail-item">
                                                                <strong>Stripe ID:</strong> 
                                                                <span className="stripe-id">{subscription.stripeId}</span>
                                                            </div>
                                                        )}
                                                        {subscription.trialEndDate && (
                                                            <div className="detail-item">
                                                                <strong>Prøveperiode slutter:</strong> {formatDate(subscription.trialEndDate)}
                                                            </div>
                                                        )}
                                                        {subscription.tierId && (
                                                            <div className="detail-item">
                                                                <strong>Nivå ID:</strong> {subscription.tierId}
                                                            </div>
                                                        )}
                                                        <div className="detail-item">
                                                            <strong>Plan:</strong> {translatePlanName(subscription.pricing?.name, subscription.product_id)}
                                                        </div>
                                                        <div className="detail-item">
                                                            <strong>Status:</strong> 
                                                            <span className={`status-tag ${subscription.status}`}>
                                                                {translateStatus(subscription.status)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="user-actions">
                            <button 
                                className="edit-button"
                                onClick={handleEdit}
                            >
                                    <i className="fas fa-pencil-alt"></i> Rediger profil
                            </button>

                                {isAdmin && (
                                    <>
                                        <button 
                                            className="reset-password-button"
                                            onClick={handleSendResetPassword}
                                            disabled={processing}
                                        >
                                            <i className="fas fa-key"></i> Send tilbakestillingslenke
                                        </button>
                                        <button 
                                            className="delete-button"
                                            onClick={() => setDeleteConfirm(user)}
                                            >
                                            <i className="fas fa-trash-alt"></i> Slett bruker
                                        </button>
                                    </>
                                )}
                            </div>

                            {/* Subscription Management Section for users viewing their own profile */}
                            {isOwnProfile && hasPermission("canManageOwnSubscription") && (
                                <div className="section">
                                    <h2 className="section-title">Mine abonnementer</h2>
                                    {error && (
                                        <div className="subscription-error">
                                            <p><i className="fas fa-exclamation-circle"></i> {error}</p>
                                        </div>
                                    )}
                                    {renderSubscriptionSection()}
                                </div>
                            )}

                            {/* Pricing Plans Section */}
                            {isOwnProfile && hasPermission("canManageOwnSubscription") && (
                            // {isOwnProfile && hasPermission("canManageOwnSubscription") && userSubscription && (userSubscription.stripe_status !== 'active' || userSubscription.stripe_status !== 'trialing') && (
                                <div className="section">
                                    <h2 className="section-title">Tilgjengelige abonnementer</h2>
                                    {renderPricingTiers()}
                                </div>
                            )}
                            
                            {/* Admin section to see all plans */}
                            {/* {isAdmin && (
                                <div className="section">
                                    <h2 className="section-title">Pricing Plans</h2>
                                    {renderPricingTiers()}
                                </div>
                            )} */}

                      
                        </div>

                        {/* Only show newsletter section for admins */}
                        {isAdmin && (
                        <div className="newsletter-section">
                            <h3>Nyhetsbrev grupper</h3>
                            {userGroups.length > 0 ? (
                                <div className="groups-list">
                                    {userGroups.map(group => (
                                        <div key={group.id} className="group-badge">
                                            <span>{group.name}</span>
                                            <button
                                                onClick={() => removeSubscriber(user.email, group.id)}
                                                className="remove-group-button"
                                                title="Fjern fra gruppe"
                                            >
                                                <i className="fas fa-times"></i>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="no-groups">Ikke påmeldt noen nyhetsbrev grupper</p>
                            )}
                        </div>
                        )}

                        {/* User Comments Section - Admin Only */}
                        {isAdmin && (
                            <div className="user-comments-section" style={{ marginTop: '2rem', padding: '1.5rem', border: '1px solid #ddd', borderRadius: '8px' }}>
                                <h3>Brukernotater</h3>
                                
                                {/* Add new comment */}
                                <div className="add-comment-form" style={{ marginBottom: '2rem' }}>
                                    <label htmlFor="newComment" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                        Legg til notat:
                                    </label>
                                    <textarea
                                        id="newComment"
                                        value={newComment}
                                        onChange={(e) => setNewComment(e.target.value)}
                                        placeholder="Skriv et notat om brukeren..."
                                        rows={4}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            fontSize: '14px',
                                            fontFamily: 'inherit',
                                            resize: 'vertical'
                                        }}
                                        disabled={processing}
                                    />
                                    <button
                                        onClick={handleCreateComment}
                                        disabled={processing || !newComment.trim()}
                                        className="save-button"
                                        style={{ marginTop: '0.5rem' }}
                                    >
                                        {processing ? 'Lagrer...' : 'Lagre notat'}
                                    </button>
                                </div>

                                {/* Display existing comments */}
                                {loadingComments ? (
                                    <div>Laster kommentarer...</div>
                                ) : userComments.length > 0 ? (
                                    <div className="comments-list">
                                        {userComments.map((comment) => (
                                            <div
                                                key={comment.iduserComments}
                                                className="comment-item"
                                                style={{
                                                    border: '1px solid #eee',
                                                    borderRadius: '6px',
                                                    padding: '1rem',
                                                    marginBottom: '1rem',
                                                    background: '#fafafa'
                                                }}
                                            >
                                                {editingCommentId === comment.iduserComments ? (
                                                    <div>
                                                        <textarea
                                                            value={editingCommentText}
                                                            onChange={(e) => setEditingCommentText(e.target.value)}
                                                            rows={4}
                                                            style={{
                                                                width: '100%',
                                                                padding: '0.75rem',
                                                                border: '1px solid #ddd',
                                                                borderRadius: '4px',
                                                                fontSize: '14px',
                                                                fontFamily: 'inherit',
                                                                resize: 'vertical',
                                                                marginBottom: '0.5rem'
                                                            }}
                                                            disabled={processing}
                                                        />
                                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                            <button
                                                                onClick={() => handleUpdateComment(comment.iduserComments)}
                                                                disabled={processing || !editingCommentText.trim()}
                                                                className="save-button"
                                                            >
                                                                {processing ? 'Lagrer...' : 'Lagre'}
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    setEditingCommentId(null);
                                                                    setEditingCommentText('');
                                                                }}
                                                                disabled={processing}
                                                                className="cancel-button"
                                                            >
                                                                Avbryt
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <p style={{ whiteSpace: 'pre-wrap', marginBottom: '0.75rem' }}>
                                                            {comment.comment}
                                                        </p>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', color: '#666' }}>
                                                            <div>
                                                                <span>Opprettet: {new Date(comment.createdAt).toLocaleString('no-NO')}</span>
                                                                {comment.lastUpdated && comment.lastUpdated !== comment.createdAt && (
                                                                    <span style={{ marginLeft: '1rem' }}>
                                                                        Oppdatert: {new Date(comment.lastUpdated).toLocaleString('no-NO')}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                                <button
                                                                    onClick={() => {
                                                                        setEditingCommentId(comment.iduserComments);
                                                                        setEditingCommentText(comment.comment);
                                                                    }}
                                                                    className="edit-button"
                                                                    style={{ padding: '0.25rem 0.5rem', fontSize: '12px' }}
                                                                >
                                                                    <i className="fas fa-edit"></i> Rediger
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDeleteComment(comment.iduserComments)}
                                                                    disabled={processing}
                                                                    className="delete-button"
                                                                    style={{ padding: '0.25rem 0.5rem', fontSize: '12px' }}
                                                                >
                                                                    <i className="fas fa-trash"></i> Slett
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p style={{ color: '#666', fontStyle: 'italic' }}>Ingen notater ennå</p>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
            {deleteConfirm && (
                <DeleteModal
                    isOpen={!!deleteConfirm}
                    onClose={() => setDeleteConfirm(null)}
                    onConfirm={() => handleDelete(deleteConfirm.idusers)}
                    itemName={`user "${deleteConfirm.name}"`}
                />
            )}
            
            
            <CreateUserModal
                isOpen={showCreateUserModal}
                onClose={handleCloseCreateUserModal}
                onConfirm={handleConfirmCreateUser}
                user={user}
                pricingPlans={pricingPlans}
                selectedPlan={selectedPlan}
                setSelectedPlan={setSelectedPlan}
                selectedTrialDuration={selectedTrialDuration}
                setSelectedTrialDuration={setSelectedTrialDuration}
                selectedTierId={selectedTierId}
                setSelectedTierId={setSelectedTierId}
                processing={processing}
                userCreationResponse={userCreationResponse}
            />
        </div>
    );
} 